import React, { useState, useRef, useContext } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaGripVertical, FaTimes, FaUndo } from "react-icons/fa";
import { GlobalContext } from "../../Utils/GlobalContext";

export const CAMPOS_AGRUPAMENTO = [
  { key: "empresa",                      label: "Empresa",            icone: "fa-building" },
  { key: "classificacao_tipo_documento", label: "Classificação",      icone: "fa-tags" },
  { key: "tipo_documento",               label: "Tipo de Documento",  icone: "fa-file-alt" },
  { key: "fornecedor",                   label: "Fornecedor",         icone: "fa-handshake" },
  { key: "situacao",                     label: "Situação",           icone: "fa-flag" },
  { key: "tipo_pagamento",               label: "Tipo de Pagamento",  icone: "fa-credit-card" },
  { key: "indice_correcao",              label: "Índice de Correção", icone: "fa-chart-line" },
  { key: "funcionario_nome",             label: "Funcionário",        icone: "fa-user" },
];

export const AGRUPAMENTO_PADRAO = ["empresa", "classificacao_tipo_documento"];

function PainelConsolidadoConfigModal({ show, onClose, niveis, onSalvar }) {
  const { darkMode } = useContext(GlobalContext);

  // niveis: array de keys (ex: ["empresa", "classificacao_tipo_documento"])
  const [selecionados, setSelecionados] = useState(() => niveis.map(k => CAMPOS_AGRUPAMENTO.find(c => c.key === k)).filter(Boolean));

  // Sincroniza quando o modal reabre
  React.useEffect(() => {
    if (show) setSelecionados(niveis.map(k => CAMPOS_AGRUPAMENTO.find(c => c.key === k)).filter(Boolean));
  }, [show, niveis]);

  const disponiveis = CAMPOS_AGRUPAMENTO.filter(c => !selecionados.find(s => s.key === c.key));

  // --- Drag state ---
  const dragRef = useRef(null); // { zone: 'avail'|'sel', idx: number }
  const [dragOver, setDragOver] = useState(null); // { zone, idx }

  const onDragStart = (zone, idx) => { dragRef.current = { zone, idx }; };
  const onDragEnd = () => { setDragOver(null); };

  const onDragOver = (e, zone, idx) => {
    e.preventDefault();
    setDragOver({ zone, idx });
  };

  const onDrop = (e, targetZone, targetIdx) => {
    e.preventDefault();
    const src = dragRef.current;
    if (!src) return;

    if (src.zone === "avail" && targetZone === "sel") {
      const item = disponiveis[src.idx];
      if (!item) return;
      const next = [...selecionados];
      next.splice(targetIdx, 0, item);
      setSelecionados(next);
    } else if (src.zone === "sel" && targetZone === "sel") {
      if (src.idx === targetIdx) return;
      const next = [...selecionados];
      const [moved] = next.splice(src.idx, 1);
      next.splice(targetIdx, 0, moved);
      setSelecionados(next);
    } else if (src.zone === "sel" && targetZone === "avail") {
      setSelecionados(selecionados.filter((_, i) => i !== src.idx));
    }
    dragRef.current = null;
    setDragOver(null);
  };

  const onDropZone = (e, zone) => {
    e.preventDefault();
    const src = dragRef.current;
    if (!src) return;
    if (src.zone === "sel" && zone === "avail") {
      setSelecionados(selecionados.filter((_, i) => i !== src.idx));
    } else if (src.zone === "avail" && zone === "sel") {
      const item = disponiveis[src.idx];
      if (item) setSelecionados([...selecionados, item]);
    }
    dragRef.current = null;
    setDragOver(null);
  };

  const adicionar = (campo) => setSelecionados([...selecionados, campo]);
  const remover = (idx) => setSelecionados(selecionados.filter((_, i) => i !== idx));
  const restaurarPadrao = () => setSelecionados(AGRUPAMENTO_PADRAO.map(k => CAMPOS_AGRUPAMENTO.find(c => c.key === k)).filter(Boolean));

  const handleSalvar = () => onSalvar(selecionados.map(c => c.key));

  const bg = darkMode ? "#1c2235" : "#fff";
  const bgSecundario = darkMode ? "#161b27" : "#f8f9fa";
  const borda = darkMode ? "#30364a" : "#dee2e6";
  const texto = darkMode ? "#e2e8f0" : "#212529";
  const textoMuted = darkMode ? "#94a3b8" : "#6c757d";

  const estiloColuna = {
    flex: 1,
    minHeight: 300,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: 12,
    borderRadius: 8,
    background: bgSecundario,
    border: `1px solid ${borda}`,
  };

  const estiloItem = (isDragging) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 6,
    background: bg,
    border: `1px solid ${borda}`,
    cursor: "grab",
    opacity: isDragging ? 0.4 : 1,
    transition: "opacity 0.15s, box-shadow 0.15s",
    fontSize: "0.875rem",
    color: texto,
  });

  const estiloDropIndicator = {
    height: 3,
    background: "#0d6efd",
    borderRadius: 2,
    margin: "2px 0",
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Modal.Header
        closeButton
        style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda }}
      >
        <Modal.Title style={{ color: texto, fontSize: "1rem" }}>
          Configurar Agrupamento
          <div style={{ fontSize: "0.75rem", fontWeight: 400, color: textoMuted, marginTop: 2 }}>
            Arraste os campos para definir a hierarquia de agrupamento da árvore
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: bg, color: texto }}>
        <div style={{ display: "flex", gap: 16 }}>

          {/* Coluna: Campos disponíveis */}
          <div
            style={{ ...estiloColuna, outline: dragOver?.zone === "avail" ? `2px dashed #0d6efd` : "none" }}
            onDragOver={(e) => { e.preventDefault(); setDragOver({ zone: "avail", idx: -1 }); }}
            onDrop={(e) => onDropZone(e, "avail")}
          >
            <div style={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: textoMuted, marginBottom: 4 }}>
              Campos Disponíveis
            </div>
            {disponiveis.length === 0 ? (
              <div style={{ color: textoMuted, fontSize: "0.82rem", textAlign: "center", paddingTop: 12 }}>
                Todos os campos foram adicionados
              </div>
            ) : (
              disponiveis.map((campo, idx) => (
                <div
                  key={campo.key}
                  draggable
                  onDragStart={() => onDragStart("avail", idx)}
                  onDragEnd={onDragEnd}
                  style={estiloItem(dragRef.current?.zone === "avail" && dragRef.current?.idx === idx)}
                >
                  <FaGripVertical style={{ color: textoMuted, flexShrink: 0 }} />
                  <i className={`fas ${campo.icone}`} style={{ color: textoMuted, width: 16, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{campo.label}</span>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    style={{ padding: "1px 8px", fontSize: "0.75rem" }}
                    onClick={() => adicionar(campo)}
                    title="Adicionar ao agrupamento"
                  >
                    +
                  </Button>
                </div>
              ))
            )}
          </div>

          {/* Coluna: Níveis de agrupamento */}
          <div
            style={{
              ...estiloColuna,
              outline: dragOver?.zone === "sel" ? `2px dashed #0d6efd` : "none",
            }}
            onDragOver={(e) => { e.preventDefault(); if (dragRef.current?.zone !== "sel") setDragOver({ zone: "sel", idx: selecionados.length }); }}
            onDrop={(e) => onDropZone(e, "sel")}
          >
            <div style={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: textoMuted, marginBottom: 4 }}>
              Níveis de Agrupamento
              <span style={{ fontWeight: 400, marginLeft: 6 }}>(arraste para reordenar)</span>
            </div>

            {selecionados.length === 0 ? (
              <div style={{ color: textoMuted, fontSize: "0.82rem", textAlign: "center", paddingTop: 20, border: `2px dashed ${borda}`, borderRadius: 6, padding: 20 }}>
                Arraste campos aqui para criar agrupamentos
              </div>
            ) : (
              selecionados.map((campo, idx) => (
                <React.Fragment key={campo.key}>
                  {/* Drop indicator acima */}
                  {((dragOver?.zone === "sel" && dragOver?.idx === idx && dragRef.current?.zone !== "sel") || (dragOver?.zone === "sel" && dragOver?.idx === idx && dragRef.current?.idx !== idx && dragRef.current?.idx !== idx - 1)) ? (
                    <div style={estiloDropIndicator} />
                  ) : null}

                  <div
                    draggable
                    onDragStart={() => onDragStart("sel", idx)}
                    onDragEnd={onDragEnd}
                    onDragOver={(e) => onDragOver(e, "sel", idx)}
                    onDrop={(e) => onDrop(e, "sel", idx)}
                    style={estiloItem(dragRef.current?.zone === "sel" && dragRef.current?.idx === idx)}
                  >
                    <FaGripVertical style={{ color: textoMuted, flexShrink: 0, cursor: "grab" }} />
                    <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: "#0d6efd",
                      color: "#fff",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}>
                      {idx + 1}
                    </span>
                    <i className={`fas ${campo.icone}`} style={{ color: textoMuted, width: 16, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{campo.label}</span>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      style={{ padding: "1px 7px", fontSize: "0.75rem" }}
                      onClick={() => remover(idx)}
                      title="Remover do agrupamento"
                    >
                      <FaTimes size={10} />
                    </Button>
                  </div>
                </React.Fragment>
              ))
            )}
          </div>
        </div>

        <div style={{ marginTop: 12, fontSize: "0.78rem", color: textoMuted, display: "flex", alignItems: "center", gap: 6 }}>
          <i className="fas fa-info-circle" />
          Se nenhum nível for configurado, todos os documentos serão listados sem agrupamento.
        </div>
      </Modal.Body>

      <Modal.Footer style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda }} className="modal-footer-padrao">
        <div className="modal-footer-acoes-esquerda">
          <Button variant="outline-secondary" size="sm" onClick={restaurarPadrao} title="Restaurar agrupamento padrão">
            <FaUndo className="me-1" size={11} />
            Restaurar Padrão
          </Button>
        </div>
        <div className="modal-footer-acoes-direita">
          <Button variant="light" className="modal-footer-btn-cancelar" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" className="modal-footer-btn-salvar" onClick={handleSalvar}>
            Aplicar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default PainelConsolidadoConfigModal;
